import React, { useState } from "react";

const SUPPORT_EMAIL = "shlomo@eiforall.com";
// Shlomo's number (5.8) — WhatsApp is the primary channel for the 55+
// audience; mailto links silently do nothing for browser-Gmail users
// (Shlomo, 6.8: "כל הלינקים לא פעילים"), so every action here uses
// links that always work in a browser: wa.me, Gmail compose, clipboard.
const WHATSAPP_NUMBER = "972544562084";
const WHATSAPP_DISPLAY = "054-4562084";

type Topic = {
  subject: string;
  label: string;
  blurb: string;
};

const TOPICS: Topic[] = [
  {
    label: "תשלום או רכישה",
    subject: "תקלה ברכישה",
    blurb: "שילמתם ולא קיבלתם גישה? יש בעיה עם פרטי החיוב? שלחו לנו את כתובת המייל שבה רכשתם.",
  },
  {
    label: "חשבונית מס",
    subject: "בקשת חשבונית מס",
    blurb:
      "החשבונית הסטנדרטית של Lemon Squeezy נשלחה אליכם מיד אחרי הרכישה. אם אתם צריכים חשבונית מס בעברית — כתבו לנו ונשלח.",
  },
  {
    label: "תקלה טכנית",
    subject: "תקלה טכנית באתר",
    blurb:
      "פרק שלא נטען? שגיאה באתר? נשמח לקבל צילום מסך וקוד תקלה אם הופיע (זה מופיע במסך התקלה).",
  },
  {
    label: "תוכן ופידבק",
    subject: "פידבק על הספר",
    blurb: "טעות בטקסט, רעיון, חוויה אישית — אנחנו תמיד שמחים לשמוע. הכותב קורא את כל המיילים.",
  },
];

// Gmail web compose — works for anyone whose mail lives in the browser
// (the vast majority of our readers). Opens in a new tab.
function gmailCompose(subject: string): string {
  return `https://mail.google.com/mail/?view=cm&fs=1&to=${SUPPORT_EMAIL}&su=${encodeURIComponent(
    subject
  )}`;
}

function whatsapp(topic: string): string {
  return `https://wa.me/${WHATSAPP_NUMBER}?text=${encodeURIComponent(
    `שלום שלמה, פונה בנושא: ${topic}. `
  )}`;
}

const actionBtn: React.CSSProperties = {
  display: "inline-flex",
  alignItems: "center",
  gap: 6,
  borderRadius: 12,
  padding: "8px 14px",
  fontSize: 13.5,
  fontWeight: 600,
  textDecoration: "none",
  cursor: "pointer",
  border: "1px solid rgba(160,112,74,0.4)",
  background: "#fff",
  color: "#A0704A",
  fontFamily: "'Heebo', system-ui, sans-serif",
};

function CopyEmailButton({ light }: { light?: boolean }): React.ReactElement {
  const [copied, setCopied] = useState(false);
  const copy = async () => {
    try {
      await navigator.clipboard.writeText(SUPPORT_EMAIL);
      setCopied(true);
      window.setTimeout(() => setCopied(false), 2500);
    } catch {
      /* clipboard unavailable — the address is visible as text anyway */
    }
  };
  return (
    <button
      type="button"
      onClick={copy}
      style={{
        ...actionBtn,
        ...(light
          ? {
              background: "rgba(255,255,255,0.15)",
              color: "#fff",
              border: "1px solid rgba(255,255,255,0.45)",
            }
          : {}),
      }}
      aria-label="העתקת כתובת המייל"
    >
      {copied ? "✓ הכתובת הועתקה" : "📋 העתקת הכתובת"}
    </button>
  );
}

export function ContactPage(): React.ReactElement {
  return (
    <div
      dir="rtl"
      style={{
        minHeight: "100vh",
        background: "#FAF7F2",
        color: "#2D1B10",
        fontFamily: "'Heebo', system-ui, sans-serif",
        padding: "32px 16px 80px",
      }}
    >
      <div style={{ maxWidth: 720, margin: "0 auto" }}>
        <a
          href="/"
          style={{
            display: "inline-block",
            marginBottom: 16,
            color: "#8B7355",
            fontSize: 13,
            textDecoration: "none",
          }}
        >
          ← חזרה לעמוד הראשי
        </a>

        <header
          style={{
            background:
              "linear-gradient(135deg,#5C3D2E 0%,#A0704A 60%,#D4875A 100%)",
            color: "white",
            borderRadius: 24,
            padding: "32px 28px",
            marginBottom: 28,
          }}
        >
          <div style={{ fontSize: 12, letterSpacing: 2, opacity: 0.7, marginBottom: 8 }}>
            יצירת קשר
          </div>
          <h1
            style={{
              fontFamily: "'Frank Ruhl Libre', serif",
              fontSize: 30,
              fontWeight: 700,
              lineHeight: 1.25,
              marginBottom: 10,
            }}
          >
            כאן בשבילכם
          </h1>
          <p style={{ fontSize: 15, lineHeight: 1.6, opacity: 0.9, maxWidth: 540 }}>
            הדרך המהירה: וואטסאפ ל-{WHATSAPP_DISPLAY}. אפשר גם במייל —{" "}
            <span style={{ fontWeight: 600, direction: "ltr", unicodeBidi: "embed" }}>
              {SUPPORT_EMAIL}
            </span>{" "}
            — אנחנו עונים אישית, בדרך כלל באותו יום.
          </p>
          <div style={{ display: "flex", flexWrap: "wrap", gap: 10, marginTop: 16 }}>
            <a
              href={whatsapp("שאלה על הסדנה")}
              target="_blank"
              rel="noreferrer"
              style={{
                ...actionBtn,
                background: "#1B8755",
                color: "#fff",
                border: "none",
                fontSize: 14.5,
                padding: "10px 18px",
              }}
            >
              💬 כתבו לנו בוואטסאפ
            </a>
            <a
              href={gmailCompose("שאלה על הספר")}
              target="_blank"
              rel="noreferrer"
              style={{
                ...actionBtn,
                background: "rgba(255,255,255,0.15)",
                color: "#fff",
                border: "1px solid rgba(255,255,255,0.45)",
              }}
            >
              ✉️ פתיחת מייל בג'ימייל
            </a>
            <CopyEmailButton light />
          </div>
        </header>

        <div
          style={{
            background: "#FFFDF9",
            border: "1px solid #E8DCC8",
            borderRadius: 16,
            padding: "20px 24px",
            marginBottom: 20,
            boxShadow: "0 4px 24px rgba(92,61,46,0.08)",
          }}
        >
          <p style={{ fontSize: 14, lineHeight: 1.7, color: "#5C3D2E", marginBottom: 8 }}>
            <strong>איך לעזור לנו לעזור לכם:</strong> אם הבעיה קשורה לרכישה, צרפו
            את כתובת המייל שבה שילמתם. אם זו תקלה טכנית — צילום מסך וקוד התקלה
            (אם הופיע) חוסכים זמן יקר.
          </p>
        </div>

        <div style={{ display: "grid", gap: 16 }}>
          {TOPICS.map((t) => (
            <div
              key={t.subject}
              style={{
                background: "#FFFDF9",
                border: "1px solid #E8DCC8",
                borderRadius: 16,
                padding: "20px 24px",
                color: "inherit",
                boxShadow: "0 4px 24px rgba(92,61,46,0.06)",
              }}
            >
              <div
                style={{
                  fontFamily: "'Frank Ruhl Libre', serif",
                  fontSize: 18,
                  fontWeight: 700,
                  color: "#5C3D2E",
                  marginBottom: 6,
                }}
              >
                {t.label}
              </div>
              <p style={{ fontSize: 14, lineHeight: 1.6, color: "#5C3D2E", margin: 0 }}>
                {t.blurb}
              </p>
              <div
                style={{
                  display: "flex",
                  flexWrap: "wrap",
                  gap: 10,
                  marginTop: 14,
                }}
              >
                <a
                  href={whatsapp(t.subject)}
                  target="_blank"
                  rel="noreferrer"
                  style={{ ...actionBtn, color: "#1B8755", border: "1px solid rgba(27,135,85,0.45)" }}
                >
                  💬 בוואטסאפ
                </a>
                <a
                  href={gmailCompose(t.subject)}
                  target="_blank"
                  rel="noreferrer"
                  style={actionBtn}
                >
                  ✉️ בג'ימייל
                </a>
              </div>
            </div>
          ))}
        </div>

        <footer
          style={{
            marginTop: 32,
            paddingTop: 20,
            borderTop: "1px solid #E8DCC8",
            fontSize: 12,
            color: "#8B7355",
            textAlign: "center",
          }}
        >
          <p style={{ marginBottom: 4 }}>
            הספר "שאלת האריה - סדנה בספר" - מאת שלמה אלפנבוים
          </p>
          <p>
            <a href="/legal/terms.html" style={{ color: "#A0704A", textDecoration: "none" }}>
              תנאי שימוש
            </a>
            {" · "}
            <a href="/legal/privacy.html" style={{ color: "#A0704A", textDecoration: "none" }}>
              פרטיות
            </a>
            {" · "}
            <a href="/legal/refund.html" style={{ color: "#A0704A", textDecoration: "none" }}>
              ביטולים
            </a>
            {" · "}
            <a
              href="/legal/accessibility.html"
              style={{ color: "#A0704A", textDecoration: "none" }}
            >
              נגישות
            </a>
          </p>
        </footer>
      </div>
    </div>
  );
}
